import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { useEffect, useMemo } from 'react'
import { ApiQueryProvider } from '@sequencehq/api'
import {
  apiQueryClient,
  apiQueryPersister,
  PERSISTED_CACHE_MAX_AGE
} from './features/api/apiQueryClient'
import {
  isProductionEnv,
  isSandboxEnv,
  isDemoEnv
} from 'lib/environment/environment'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes
} from 'react-router-dom'
import { StytchB2BProvider } from '@stytch/react/b2b'
import { stytchClient } from 'lib/auth/stytch'
import {
  SequenceThemeProvider,
  getStoredConsent
} from '@sequencehq/core-components'
import { DashboardConsentBanner } from 'lib/consent'
import GlobalStyles from 'components/GlobalStyles'
import { PaymentsApp } from 'modules/PaymentsApp/PaymentsApp'
import { LoginApp } from 'modules/LoginApp/LoginApp'
import { DashboardApp } from 'modules/DashboardApp/DashboardApp'
import { PostHogProvider } from 'posthog-js/react'
import { initializeApiClients } from 'features/api/initializeApiClients'
import { getSentryConfig } from 'features/sentry/getSentryConfig'
import { sentryIgnoredErrors } from 'features/sentry/ignoredErrors'
import posthog from 'posthog-js'
import type { PostHogInterface } from 'posthog-js'

const { dsn, environment } = getSentryConfig()

const posthogKey = import.meta.env.VITE_POSTHOG_API_KEY ?? ''
const posthogApiHost = import.meta.env.VITE_POSTHOG_API_HOST ?? ''
/*
 * Putting extra Sentry config separately here because:
 * 1. `getSentryConfig` is used by multiple apps, but only dashboard-web will be using these for now.
 * 2. All the integration tests running on GHA break when the vars are added to `getSentryConfig`.
 *    We can't figure out how/why/what is missing (presumably some env var declarations), so it's easier for now to just have these separate.
 */
const sentryOrganisationUrl = import.meta.env.VITE_SENTRY_ORGANISATION_URL
const sentryProjectId = import.meta.env.VITE_SENTRY_PROJECT_ID

const isSandboxOrProduction = ['sandbox', 'production'].includes(environment)
const isDemo = ['demo'].includes(environment)
const isDeployed = [
  'dev',
  'stable',
  'test',
  'sandbox',
  'production',
  'demo'
].includes(environment)

if (isDeployed) {
  const integrations = [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ]

  // Session replay is consent-gated; error reporting below stays always-on. To
  // gate error reporting too, gate Sentry.init rather than only replay here.
  const replayConsentGiven = getStoredConsent()?.status === 'accepted'
  if (isSandboxOrProduction && replayConsentGiven) {
    integrations.push(Sentry.replayIntegration({ maskAllText: false }))
  }

  if (isSandboxOrProduction || isDemo) {
    if (!Number.isNaN(sentryProjectId)) {
      integrations.push(
        posthog.sentryIntegration({
          organization: sentryOrganisationUrl,
          projectId: Number.parseInt(sentryProjectId)
        })
      )
    }
  }

  Sentry.init({
    ...(isSandboxOrProduction
      ? { replaysSessionSampleRate: 0.01, replaysOnErrorSampleRate: 1.0 }
      : {}),
    dsn,
    environment,
    integrations,
    ignoreErrors: sentryIgnoredErrors,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.001
  })
}

initializeApiClients()

const AppRoot = () => {
  const SubApp = useMemo(() => {
    if (window.location.pathname.startsWith('/pay')) {
      return PaymentsApp
    }

    if (window.location.pathname.startsWith('/auth')) {
      return LoginApp
    }

    return DashboardApp
  }, [])

  const posthogOptions = useMemo(() => {
    if (posthogApiHost) {
      // opt_in_capturing() on the apply path enables capture AND persistence
      // together, so gate both the same way here to keep the two entry points consistent.
      const userHasConsentedToCapture =
        getStoredConsent()?.status === 'accepted'
      return {
        api_host: posthogApiHost,
        // api_host is our ingestion proxy, which can't serve the PostHog app. ui_host points
        // links into the app (session replay, toolbar) at the real EU dashboard so they open.
        ui_host: 'https://eu.posthog.com',
        defaults: '2026-01-30' as const,
        capture_pageview: false,
        // No cookies/capture until consent; seeded from stored consent so a
        // returning accepter starts opted-in. useConsentSync handles in-session changes.
        opt_out_capturing_by_default: !userHasConsentedToCapture,
        // Gates posthog's own cookies/localStorage too, not just capture.
        opt_out_persistence_by_default: !userHasConsentedToCapture,
        // posthog's persisted opt-in outlives a CONSENT_VERSION bump; re-gate it.
        loaded: (ph: PostHogInterface) => {
          if (!userHasConsentedToCapture && ph.has_opted_in_capturing()) {
            ph.opt_out_capturing()
          }
        },
        disable_session_recording:
          import.meta.env.VITEST ||
          !(isProductionEnv() || isSandboxEnv() || isDemoEnv()) ||
          window.location.hostname.includes('preview.seqhq.io')
      }
    }

    return
  }, [])

  return (
    <ApiQueryProvider
      client={apiQueryClient}
      persister={apiQueryPersister}
      maxAge={PERSISTED_CACHE_MAX_AGE}
    >
      <SequenceThemeProvider>
        <GlobalStyles />
        <DashboardConsentBanner />
        <StytchB2BProvider stytch={stytchClient}>
          {posthogKey ? (
            <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
              <SubApp />
            </PostHogProvider>
          ) : (
            <SubApp />
          )}
        </StytchB2BProvider>
      </SequenceThemeProvider>
    </ApiQueryProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <AppRoot />
)
